import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogForm, InfoTooltip } from '../../../shared/components/common';
import { DimensionValuePicker } from '../../../shared/components/accounting/DimensionValuePicker';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Switch } from '../../../components/ui/switch';
import type {
  Dimension,
  DimensionValue,
  DimensionValueCreate,
  DimensionValueUpdate,
} from '../types';

interface DimensionValueFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: DimensionValueCreate | DimensionValueUpdate) => Promise<void>;
  dimension: Dimension;
  /** Editing an existing value, or null to create. */
  value?: DimensionValue | null;
  /** Pre-selected parent (for "add child" actions). */
  defaultParentId?: string | null;
  /** Seed the name field when creating (e.g. text typed in a picker search). */
  defaultName?: string;
  isLoading?: boolean;
}

export function DimensionValueFormDialog({
  open,
  onClose,
  onSubmit,
  dimension,
  value,
  defaultParentId = null,
  defaultName,
  isLoading,
}: DimensionValueFormDialogProps) {
  const { t } = useTranslation('accounting');
  const isEditing = !!value;
  const hierarchical = dimension.is_hierarchical;

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [parentValueId, setParentValueId] = useState('');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (open) {
      setName(value?.name ?? defaultName ?? '');
      setCode(value?.code ?? '');
      setDescription(value?.description ?? '');
      setParentValueId(value?.parent_value_id ?? defaultParentId ?? '');
      setIsActive(value?.is_active ?? true);
    }
  }, [open, value, defaultParentId, defaultName]);

  const handleSubmit = async () => {
    const parent = hierarchical ? parentValueId || null : null;
    if (isEditing) {
      const update: DimensionValueUpdate = {
        name: name.trim(),
        description: description.trim() || null,
        parent_value_id: parent,
        is_active: isActive,
      };
      await onSubmit(update);
    } else {
      const create: DimensionValueCreate = {
        name: name.trim(),
        code: code.trim() || null,
        description: description.trim() || null,
        parent_value_id: parent,
        is_active: isActive,
      };
      await onSubmit(create);
    }
  };

  const canSubmit = !isLoading && name.trim().length > 0;

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={isEditing ? t('dimensions.editValue') : t('dimensions.createValue')}
      onSubmit={handleSubmit}
      submitLabel={isEditing ? t('common:app.save') : t('common:app.create')}
      cancelLabel={t('common:app.cancel')}
      isLoading={isLoading}
      isDisabled={!canSubmit}
      maxWidth="sm"
    >
      <div className="space-y-4 mt-2">
        <div className="space-y-2">
          <Label>
            {t('dimensions.name')} <span className="text-destructive">*</span>
          </Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('dimensions.valueNamePlaceholder')}
            autoFocus
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-1.5">
            <Label>{t('dimensions.code')}</Label>
            <InfoTooltip content={t('dimensions.codeHelp')} />
          </div>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={t('dimensions.codePlaceholder')}
            disabled={isEditing}
          />
        </div>

        {hierarchical && (
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label>{t('dimensions.parentValue')}</Label>
              <InfoTooltip content={t('dimensions.parentValueHelp')} />
            </div>
            <DimensionValuePicker
              dimensionId={dimension.id}
              value={parentValueId}
              onChange={setParentValueId}
              placeholder={t('dimensions.noParentRoot')}
              allowClear
              // Prevent assigning a node under itself or its own descendants.
              excludeSubtreeOf={value?.id}
              // This picker is already inside the value-create flow; nesting a
              // second create dialog here would be confusing.
              hideAddNew
            />
          </div>
        )}

        <div className="space-y-2">
          <Label>{t('dimensions.description')}</Label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
          />
        </div>

        <div className="flex items-center justify-between rounded-md border p-3">
          <Label>{t('dimensions.active')}</Label>
          <Switch checked={isActive} onCheckedChange={setIsActive} />
        </div>
      </div>
    </DialogForm>
  );
}
